import { logger } from '@/utils/logger';

export const reOrderNodes = ({ recipe, nodes, edges }) => {
  return reorderJsonStatements(recipe);
};

function reorderJsonStatements(jsonObj) {
  // Create a deep copy to avoid modifying the original
  const jsonCopy = JSON.parse(JSON.stringify(jsonObj));

  if (
    !jsonCopy.statements ||
    !jsonCopy.statements.statement ||
    !Array.isArray(jsonCopy.statements.statement)
  ) {
    return jsonCopy; // Nothing to reorder
  }

  const statements = jsonCopy.statements.statement;

  // Build dependency map and collect all statement IDs
  const dependencies = {}; // Maps statement ID to IDs it depends on
  const idToIndex = {}; // Maps statement ID to its current index
  const allIds = new Set(); // All statement IDs

  // First pass: collect all IDs and build ID to index mapping
  statements.forEach((stmt, index) => {
    const id = stmt['@id'];
    if (id) {
      allIds.add(id);
      idToIndex[id] = index;
    }
  });

  // Second pass: identify dependencies
  statements.forEach((stmt) => {
    const id = stmt['@id'];
    if (!id) return;

    dependencies[id] = new Set();

    // Check all possible reference fields
    const refFields = ['@ref-dataframe-id', '@ref-connector-id'];

    refFields.forEach((field) => {
      if (stmt[field] && allIds.has(stmt[field])) {
        dependencies[id].add(stmt[field]);
      }
    });

    // Also look for nested references in allReferences array
    if (stmt.allReferences && Array.isArray(stmt.allReferences)) {
      stmt.allReferences.forEach((ref) => {
        if (ref.id && allIds.has(ref.id)) {
          dependencies[id].add(ref.id);
        }
      });
    }
  });

  // Perform topological sort with proximity preservation
  const visited = new Set();
  const result = [];

  // DFS function for topological sort
  function visit(id, path = new Set()) {
    // Skip if already visited
    if (visited.has(id)) return;

    // Check for cycles
    if (path.has(id)) {
      logger.warn(`Cycle detected involving node: ${id}`);
      return;
    }

    // Mark as being processed
    path.add(id);

    // Process dependencies first
    if (dependencies[id]) {
      // Sort dependencies by their current proximity to this node
      const deps = Array.from(dependencies[id]);
      deps.sort((a, b) => {
        // Prefer dependencies that are closer to the current node
        const distA = Math.abs(idToIndex[a] - idToIndex[id]);
        const distB = Math.abs(idToIndex[b] - idToIndex[id]);
        return distA - distB;
      });

      // Visit all dependencies
      for (const depId of deps) {
        visit(depId, new Set(path));
      }
    }

    // Mark as visited
    visited.add(id);

    // Add to result
    const originalIndex = idToIndex[id];
    if (originalIndex !== undefined) {
      result.push(statements[originalIndex]);
    }

    // Remove from path
    path.delete(id);
  }

  // Process all statements
  for (const id of allIds) {
    visit(id);
  }

  // Add any statements without IDs at the end
  statements.forEach((stmt) => {
    if (!stmt['@id'] || !visited.has(stmt['@id'])) {
      result.push(stmt);
    }
  });

  // Update the statements array
  jsonCopy.statements.statement = result;

  return jsonCopy;
}
